import { ChangeHandler } from 'react-hook-form'
import { PlanPurchaseFormFieldWrapperProps } from './PlanPurchaseFormFieldWrapperProps'
import { deserializePlanIntervalQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planInterval/deserializePlanIntervalQueryParam'
import { useInternalPlanPurchaseFormContext } from '../PlanPurchaseForm/context/useInternalPlanPurchaseFormContext'
import React, { Ref, cloneElement } from 'react'
import useForkRef from '@mui/utils/useForkRef'

const valueAsMap: Record<
  PlanPurchaseFormFieldWrapperProps['name'],
  ((value: string) => unknown) | undefined
> = {
  paymentElementComplete: undefined,
  planInterval: deserializePlanIntervalQueryParam,
  billingDetails: undefined,
  promoCode: undefined,
  orgName: undefined,
  planExtraDetails: undefined
}

/**
 * Wraps a form field to provide react-hook-form functionality, such
 * as wiring up the value and event handlers, and providing validation
 * errors to the helperText (overriding other helper text).
 */
export function PlanPurchaseFormFieldWrapper({
  name,
  children
}: PlanPurchaseFormFieldWrapperProps): JSX.Element {
  const {
    form: {
      formState: { errors },
      register
    }
  } = useInternalPlanPurchaseFormContext()

  const error = errors[name]
  const valueAs = valueAsMap[name]
  const { ref, ...formProps } = register(name, { setValueAs: valueAs })

  const child = React.Children.only(children)
  const handleRef = useForkRef('ref' in child ? (child.ref as Ref<unknown>) : undefined, ref)
  const clone = cloneElement(child, {
    ...formProps,
    helperText: error?.message || child.props.helperText,
    error: Boolean(error?.message),
    onChange: async (...args: Parameters<ChangeHandler>) => {
      await formProps.onChange(...args)
      await child.props.onChange?.(...args)
    },
    ref: handleRef ?? undefined
  })

  return <>{clone}</>
}
